import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  skip,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs'
import { FEAT_BFF_SEARCH_PAGE_ID, FEAT_BFF_SEARCH_PAGE_VARIATION } from '../shared/experimentation/experiment.model'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'
import { SearchDataService } from '../singleton-services/search-data.service'
import { ActivatedRoute, ParamMap } from '@angular/router'
import { MessageModel, SEOMetadata } from '@flocasts/experience-service-types'
import { isMessageModel, isMultipleSectionsViewModel } from '../shared/functions/type-guards'
import { DEFAULT_OG_IMAGE_HEIGHT, DEFAULT_OG_IMAGE_WIDTH, PageMetaService } from '../libs/page-meta/page-meta.service'
import { ListPageProperties, SegmentTagV2 } from '../shared/analytics/models/segment-tag.model'
import { SEARCH_LIST_TAG } from '../libs/_deprecated/search/old-search-analytics.service'
import { SegmentService } from '../shared/analytics/services/segment.service'

@Component({
  selector: 'flo-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchComponent implements OnDestroy {
  constructor(
    private readonly searchDataService: SearchDataService,
    private readonly experimentationService: ExperimentationService,
    private readonly ar: ActivatedRoute,
    private readonly pageMetaService: PageMetaService,
    private readonly segmentService: SegmentService
  ) {}

  public readonly isLayoutError$ = new BehaviorSubject<boolean>(false)
  public readonly isPartialError$ = new BehaviorSubject<boolean>(false)
  public readonly isLoading$ = new BehaviorSubject<boolean>(true)
  private readonly ngOnDestroy$ = new Subject<void>()

  public readonly bffSearchFeatureFlag$ = this.experimentationService.isInVariation(
    FEAT_BFF_SEARCH_PAGE_ID,
    FEAT_BFF_SEARCH_PAGE_VARIATION
  )

  private readonly routeQueryParams$ = this.ar.queryParamMap.pipe(
    distinctUntilChanged(
      (x: ParamMap, y: ParamMap) =>
        x.get('search') === y.get('search') &&
        x.get('offset') === y.get('offset') &&
        x.get('limit') === y.get('limit') &&
        x.get('type') === y.get('type')
    ),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  public searchLayout$ = combineLatest([this.bffSearchFeatureFlag$, this.routeQueryParams$]).pipe(
    switchMap(([isBffSearch, queryParams]) => {
      if (isBffSearch) {
        return this.searchDataService.getSearchLayout(queryParams).pipe(
          tap(searchLayoutRes => {
            !searchLayoutRes ? this.isLayoutError$.next(true) : this.isLayoutError$.next(false)
          })
        )
      }
      return of(undefined)
    }),
    tap(_ => this.isLoading$.next(false)),
    // Only listen to the first value emitted by the observable.
    // This observable will handle the initial data rendering on page load, and the searchPartial$
    // observable will handle the subsequent partial data rendering whenever a user interacts with
    // the search filters.
    take(1)
  )

  public searchPartial$ = combineLatest([this.bffSearchFeatureFlag$, this.routeQueryParams$]).pipe(
    tap(_ => this.isLoading$.next(true)),
    switchMap(([isBffSearch, queryParams]) => {
      if (isBffSearch) {
        return this.searchDataService.getSearchFilterablePartial(queryParams).pipe(
          tap(searchPartialRes => {
            !searchPartialRes ? this.isPartialError$.next(true) : this.isPartialError$.next(false)
          })
        )
      }
      return of(undefined)
    }),
    tap(_ => this.isLoading$.next(false)),
    // Skip the first value emitted by the observable and only listen to subsequent values.
    // The searchLayout$ observable will handle the initial data rendering on page load,
    // and this observable will handle the subsequent partial data rendering when a user
    // interacts with the search filters
    skip(1)
  )

  public message$: Observable<MessageModel | undefined> = this.searchLayout$.pipe(
    map(res => {
      if (res && isMultipleSectionsViewModel(res)) {
        return res.sections.find(isMessageModel)
      }
      return
    })
  )

  private setPageMeta(seo: SEOMetadata): void {
    this.pageMetaService.setPageMeta({
      type: 'website',
      twitterCard: 'summary_large_image',
      title: seo.title || 'Search',
      description: seo.description || '',
      canonicalUrl: seo.canonicalUrl || '',
      siteName: null,
      image: {
        url: seo.imageUrl || '',
        height: Number(seo.imageHeight) || DEFAULT_OG_IMAGE_HEIGHT,
        width: Number(seo.imageWidth) || DEFAULT_OG_IMAGE_WIDTH
      }
    })
  }

  private seoPageMeta$ = this.searchLayout$
    .pipe(
      take(1),
      map(res => res?.seo),
      tap(seo => {
        if (!!seo) {
          this.setPageMeta(seo)
        }
      })
    )
    .subscribe()

  private analyticsPageCall$ = combineLatest([this.searchLayout$, this.bffSearchFeatureFlag$])
    .pipe(
      take(1),
      // Only make this analytics page call if we are utilizing the BFF search flow,
      // otherwise we will rely on the page call in the old component.
      // This can be removed at whatever point this is no longer driven by a feature flag
      // and we are fully on the BFF search experience.
      filter(([res, isBffSearch]) => !!res && isBffSearch),
      map(([res, _]) => res),
      map(res => res?.analytics),
      tap(analytics => {
        const segmentTag: SegmentTagV2 = {
          ...SEARCH_LIST_TAG,
          page_category: analytics?.extraContext?.pageCategory || 'Search'
        }
        const segmentPageProperties: ListPageProperties = { _tag: 'list' }
        this.segmentService.pageV2(segmentTag, segmentPageProperties)
      })
    )
    .subscribe()

  ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
    this.isLayoutError$.complete()
    this.isPartialError$.complete()
    this.isLoading$.complete()
    this.pageMetaService.clearMeta()
  }
}
